// apps/admin/src/components/auth/MfaEnrollmentCard.tsx
//
// Native GCIP TOTP enrolment card for the founder admin portal: QR + manual key
// + 6-digit verify + success. Operates on a Firebase `User` only. Reuses the
// admin AppCheckRecoveryBanner on an App Check / reCAPTCHA throttle (the
// 2026-06-12 lockout lesson); a wrong code is an inline retry with red boxes; a
// network failure, rate limit or unknown error at verify time is a plain message
// with the boxes left neutral (finding M2, 2026-08-17: it was reported as a
// wrong code); the one-factor ceiling means the account is ALREADY enrolled, so
// it says so and offers Continue; a stale session (auth/requires-recent-login)
// prompts a re-sign-in, and an expired MFA session offers "Try again".

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import type { User } from 'firebase/auth';
import { Lock, Check, Info, Copy, Loader2 } from 'lucide-react';
import MfaCodeInput from './MfaCodeInput';
import AppCheckRecoveryBanner from './AppCheckRecoveryBanner';
import {
  beginTotpEnrollment,
  completeTotpEnrollment,
  type TotpEnrollmentInit,
} from '../../lib/nativeMfa';
import {
  ALREADY_ENROLLED_MESSAGE,
  GENERATE_ALREADY_ENROLLED_MESSAGE,
  classifyMfaFailure,
  mfaFailureMessage,
  readErrorMessage,
} from '../../lib/mfaErrors';
import {
  classifyAuthError,
  clearAppCheckThrottleState,
  type AppCheckErrorContext,
} from '../../lib/appCheckRecovery';

interface MfaEnrollmentCardProps {
  user: User;
  onEnrolled: () => void | Promise<void>;
  onReauth?: () => void;
  title?: string;
  subtitle?: string;
}

type Stage = 'loading' | 'entry' | 'success' | 'already_enrolled';

const GENERATE_FALLBACK_ERROR = 'Could not start two-factor setup. Please try again.';

const MfaEnrollmentCard: React.FC<MfaEnrollmentCardProps> = ({
  user,
  onEnrolled,
  onReauth,
  title = 'Set up two-factor authentication',
  subtitle = 'Founder portal access requires a second step at sign-in. Set it up now to continue.',
}) => {
  const [stage, setStage] = useState<Stage>('loading');
  const [init, setInit] = useState<TotpEnrollmentInit | null>(null);
  const [code, setCode] = useState<string>('');
  const [verifying, setVerifying] = useState<boolean>(false);
  /** A wrong / expired code: red boxes, cleared for retry. */
  const [codeError, setCodeError] = useState<string>('');
  /** A verify-time failure that was NOT the code's fault: message only, boxes stay neutral. */
  const [verifyError, setVerifyError] = useState<string>('');
  const [fatalError, setFatalError] = useState<string>('');
  /**
   * Whether the fatal panel offers "Try again". Default true: most fatal states
   * (a dead setup session, a transient generate failure) are cleared by
   * regenerating. `email_unverified` is NOT: nothing this app can do changes
   * `emailVerified`, so a retry button there is a loop that cannot succeed.
   */
  const [fatalRetryable, setFatalRetryable] = useState<boolean>(true);
  /**
   * Which phase produced the `already_enrolled` stage. The copy differs:
   * nothing was scanned at generate time, so the verify-time wording would
   * describe an action the founder never took.
   */
  const [alreadyEnrolledPhase, setAlreadyEnrolledPhase] =
    useState<'generate' | 'verify'>('verify');
  const [appCheckCtx, setAppCheckCtx] = useState<AppCheckErrorContext | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [resetNonce, setResetNonce] = useState<number>(0);
  const startedRef = useRef<boolean>(false);
  const alreadyEnrolledHeadingRef = useRef<HTMLHeadingElement | null>(null);

  const handleFailure = useCallback((err: unknown, phase: 'generate' | 'verify'): void => {
    const ctx = classifyAuthError(err);
    if (ctx.isAppCheckFailure) {
      setAppCheckCtx(ctx);
      return;
    }
    const failure = classifyMfaFailure(err);
    const message = mfaFailureMessage(failure, 'enrol');
    switch (failure.kind) {
      case 'requires_recent_login':
      case 'session_expired':
        // Both need a restart; "Try again" regenerates, "Sign in again" re-auths.
        setFatalError(message);
        return;
      case 'already_enrolled':
        // Terminal on BOTH phases, and NOT the fatal branch: the account already
        // holds its one allowed factor, so "Try again" can only fail again.
        // Generate time is the invitation-resume shape (GCIP refuses to issue a
        // second secret); verify time is the one-factor ceiling on submit. Both
        // end at Continue; only the copy differs.
        setAlreadyEnrolledPhase(phase);
        setStage('already_enrolled');
        return;
      case 'email_unverified':
        // Terminal on BOTH phases: GCIP will not begin enrolment on an
        // unverified email, and the portal has no verification surface. Name the
        // blocker, offer no retry.
        setFatalRetryable(false);
        setFatalError(message);
        return;
      case 'blocked':
        // The account or the provider is off: nothing here can move past it,
        // but "Try again" stays on screen so the founder is not trapped.
        setFatalError(message);
        return;
      case 'network':
      case 'rate_limited':
        if (phase === 'verify') {
          setVerifyError(message);
        } else {
          setFatalError(message);
        }
        return;
      case 'wrong_code':
        if (phase === 'verify') {
          // Wrong / expired code: stay on the entry screen, clear the boxes, retry.
          setCodeError(message);
          setResetNonce((n) => n + 1);
          setCode('');
          return;
        }
        break;
      default:
        break;
    }
    // Unknown (or a code that cannot belong to this phase): do not blame the code.
    if (phase === 'verify') {
      setVerifyError(message);
      return;
    }
    setFatalError(readErrorMessage(err) || GENERATE_FALLBACK_ERROR);
  }, []);

  const start = useCallback(async (): Promise<void> => {
    setStage('loading');
    setFatalError('');
    setFatalRetryable(true);
    setAlreadyEnrolledPhase('verify');
    setAppCheckCtx(null);
    try {
      const result = await beginTotpEnrollment(user);
      setInit(result);
      setStage('entry');
    } catch (err) {
      handleFailure(err, 'generate');
      // Do NOT clobber a terminal stage handleFailure just set. This line is
      // what used to force a generate-time `already_enrolled` back to an empty
      // entry screen, which is why that case had to fall through to the fatal
      // branch at all.
      setStage((current) => (current === 'already_enrolled' ? current : 'entry'));
    }
  }, [user, handleFailure]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void start();
  }, [start]);

  // The already-enrolled panel replaces the whole card, so keyboard and screen
  // reader focus must follow it rather than stay on a button that is gone.
  useEffect(() => {
    if (stage === 'already_enrolled') {
      alreadyEnrolledHeadingRef.current?.focus();
    }
  }, [stage]);

  const handleVerify = useCallback(async (submitCode: string = code): Promise<void> => {
    if (!init || submitCode.length !== 6 || verifying) return;
    setVerifying(true);
    setCodeError('');
    setVerifyError('');
    try {
      await completeTotpEnrollment(user, init.secret, submitCode);
      setStage('success');
    } catch (err) {
      handleFailure(err, 'verify');
    } finally {
      setVerifying(false);
    }
  }, [init, code, verifying, user, handleFailure]);

  const handleCopyKey = useCallback(async (): Promise<void> => {
    if (!init) return;
    try {
      await navigator.clipboard.writeText(init.sharedSecretKey.replace(/\s/g, ''));
    } catch {
      // Clipboard unavailable — the key is visible for manual entry.
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }, [init]);

  if (appCheckCtx) {
    return (
      <div className="w-full max-w-md p-8 rounded-2xl shadow-2xl bg-slate-800/70 backdrop-blur-xl border border-white/10 z-10">
        <AppCheckRecoveryBanner
          ctx={appCheckCtx}
          onReset={async () => {
            await clearAppCheckThrottleState();
            window.location.reload();
          }}
        />
      </div>
    );
  }

  if (stage === 'already_enrolled') {
    return (
      <div className="w-full max-w-md p-8 rounded-2xl shadow-2xl bg-slate-800/70 backdrop-blur-xl border border-white/10 z-10 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 mb-4">
          <Check size={30} className="text-white" />
        </div>
        <h2
          ref={alreadyEnrolledHeadingRef}
          tabIndex={-1}
          className="text-2xl font-bold text-white mb-2 focus:outline-none"
        >
          Two-factor authentication is already on
        </h2>
        {/* role="status" (polite by default): the panel already took focus, so
            an assertive live region would interrupt the heading announcement. */}
        <p className="text-slate-400 text-sm mb-6 leading-relaxed" role="status">
          {alreadyEnrolledPhase === 'generate'
            ? GENERATE_ALREADY_ENROLLED_MESSAGE
            : ALREADY_ENROLLED_MESSAGE}
        </p>
        <button
          type="button"
          onClick={() => void onEnrolled()}
          className="w-full py-3 px-4 font-semibold text-white bg-gradient-to-r from-emerald-600 to-green-600 rounded-lg hover:from-emerald-700 hover:to-green-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-emerald-500"
        >
          Continue to the portal
        </button>
      </div>
    );
  }

  if (stage === 'success') {
    return (
      <div className="w-full max-w-md p-8 rounded-2xl shadow-2xl bg-slate-800/70 backdrop-blur-xl border border-white/10 z-10 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 mb-4">
          <Check size={30} className="text-white" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Two-factor authentication is on</h2>
        <p className="text-slate-400 text-sm mb-6 leading-relaxed">
          Your account now asks for a code at every sign-in. That&rsquo;s the whole setup.
        </p>
        <div className="bg-blue-500/15 border border-blue-500/40 text-blue-100 p-4 rounded-lg mb-6 flex items-start text-sm leading-relaxed">
          <Info size={18} className="mr-3 flex-shrink-0 mt-0.5 text-blue-300" />
          <span>
            Keep your authenticator app safe. If you lose the device, use the audited admin
            break-glass reset with your off-device recovery.
          </span>
        </div>
        <button
          type="button"
          onClick={() => void onEnrolled()}
          className="w-full py-3 px-4 font-semibold text-white bg-gradient-to-r from-emerald-600 to-green-600 rounded-lg hover:from-emerald-700 hover:to-green-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-emerald-500"
        >
          Continue to the portal
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md p-8 rounded-2xl shadow-2xl bg-slate-800/70 backdrop-blur-xl border border-white/10 z-10">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 mb-4">
          <Lock size={30} className="text-white" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">{title}</h2>
        <p className="text-slate-400 text-sm leading-relaxed">{subtitle}</p>
      </div>

      <div className="bg-blue-500/15 border border-blue-500/40 text-blue-100 p-4 rounded-lg mb-6 flex items-start text-sm leading-relaxed">
        <Info size={18} className="mr-3 flex-shrink-0 mt-0.5 text-blue-300" />
        <span>
          Scan the code with an authenticator app (Google Authenticator, 1Password, Authy). You&rsquo;ll
          enter a fresh 6-digit code each time you sign in.
        </span>
      </div>

      {fatalError && (
        <div
          className="bg-red-500/15 border border-red-500/40 text-red-200 p-4 rounded-lg mb-6 text-sm"
          role="alert"
          aria-live="assertive"
        >
          <p>{fatalError}</p>
          <div className="mt-3 flex gap-3">
            {fatalRetryable && (
              <button
                type="button"
                onClick={() => void start()}
                className="text-emerald-300 hover:text-emerald-200 underline underline-offset-2"
              >
                Try again
              </button>
            )}
            {onReauth && (
              <button
                type="button"
                onClick={onReauth}
                className="text-emerald-300 hover:text-emerald-200 underline underline-offset-2"
              >
                Sign in again
              </button>
            )}
          </div>
        </div>
      )}

      {stage === 'loading' && (
        <div className="flex items-center justify-center py-10 text-slate-400">
          <Loader2 size={18} className="animate-spin mr-2" />
          <span className="text-sm">Preparing your setup code&hellip;</span>
        </div>
      )}

      {stage === 'entry' && init && (
        <>
          <div className="flex gap-5 items-center mb-6 flex-col sm:flex-row">
            <div className="flex-none bg-white rounded-xl p-2.5">
              <QRCodeSVG value={init.otpauthUrl} size={128} />
            </div>
            <div className="text-sm text-slate-400 leading-relaxed">
              Can&rsquo;t scan? Enter this key by hand:
              <div className="mt-2 flex items-center justify-between gap-2 font-mono text-sm text-white bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 tracking-wider">
                <span className="break-all">{init.sharedSecretKey}</span>
                <button
                  type="button"
                  onClick={() => void handleCopyKey()}
                  className="flex-none inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-xs"
                  aria-label="Copy setup key"
                >
                  <Copy size={13} />
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          </div>

          <label className="block mb-2 text-sm font-medium text-slate-300">
            Enter the 6-digit code from your app
          </label>
          <MfaCodeInput
            ariaLabel="Setup verification code"
            onChange={setCode}
            onComplete={(v) => void handleVerify(v)}
            disabled={verifying}
            error={!!codeError}
            resetNonce={resetNonce}
          />
          <p
            className={`text-xs text-center mt-2 mb-5 min-h-[1rem] ${codeError || verifyError ? 'text-red-400' : 'text-slate-500'}`}
            role={codeError || verifyError ? 'alert' : undefined}
            aria-live={codeError || verifyError ? 'assertive' : undefined}
          >
            {codeError || verifyError || '6 digits from your authenticator'}
          </p>

          <button
            type="button"
            onClick={() => void handleVerify()}
            disabled={code.length !== 6 || verifying}
            className="w-full py-3 px-4 font-semibold text-white bg-gradient-to-r from-emerald-600 to-green-600 rounded-lg hover:from-emerald-700 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-emerald-500"
            aria-busy={verifying}
          >
            {verifying ? (
              <>
                <Loader2 size={18} className="animate-spin mr-2" />
                <span>Verifying&hellip;</span>
              </>
            ) : (
              <span>Verify and turn on</span>
            )}
          </button>
        </>
      )}
    </div>
  );
};

export default MfaEnrollmentCard;
